import { useState } from 'react';
import { cn, getFullLevelThumbnail } from '~/util';
import { useFieldContext } from '~/util/hooks/useFormContext';
import { BaseLevel } from '~/util/types/levels';
import { LevelsSelector } from '../../ItemSelector';
import {
    FieldLabelWithError,
    FieldWithErrorProps,
} from './FieldLabelWithError';
import { useDisableFormContext } from '../DisableContext';

export function LevelThumbnailField({
    availableLevelsToPick,
    defaultLevelID,
    classname,
    forceDisabled,
    ...labelProps
}: {
    availableLevelsToPick: BaseLevel[];
    defaultLevelID: number;
    forceDisabled?: boolean;
    classname?: string;
} & FieldWithErrorProps) {
    const field = useFieldContext<number | null>();
    const [backgroundSelectorOpen, setBackgroundSelectorOpen] = useState(false);
    const disabled = useDisableFormContext() || forceDisabled;

    const levelID =
        field.state.value !== null && field.state.value !== 0
            ? field.state.value
            : (defaultLevelID ?? null);

    const level = availableLevelsToPick.find(
        (level) => level.level_id === levelID,
    );

    return (
        <FieldLabelWithError<BaseLevel> {...labelProps}>
            <p className="text-secondary-light text-sm leading-snug">
                You can select here the background image that should be
                displayed on your profile, among the levels you&apos;ve beaten.
                If you don&apos;t select any, your hardest beaten level will be
                used as the default one.
            </p>

            <div className="w-full flex justify-center">
                <button
                    disabled={disabled}
                    type="button"
                    onClick={() => setBackgroundSelectorOpen(true)}
                    className={cn(
                        'group max-w-xs w-full border border-border rounded-xl overflow-hidden transition-all duration-200 transform hover:scale-[1.015] hover:shadow-md hover:border-primary cursor-pointer',
                        disabled && 'opacity-50 cursor-not-allowed',
                        classname,
                    )}
                >
                    <div className="relative">
                        <img
                            src={
                                levelID != null
                                    ? getFullLevelThumbnail(levelID)
                                    : undefined
                            }
                            alt="Profile background"
                            className="w-full object-cover aspect-video"
                        />
                        <div className="absolute bottom-0 w-full bg-black/50 text-white text-center text-sm py-1 px-2">
                            {level
                                ? `${level.name}${levelID === defaultLevelID ? ' (Default)' : ''}`
                                : 'None'}
                        </div>
                    </div>
                </button>
            </div>

            <LevelsSelector
                customLevels={availableLevelsToPick}
                open={backgroundSelectorOpen}
                onOpenChange={() => setBackgroundSelectorOpen(false)}
                initial={level}
                single
                onSave={(selectedLevel) => {
                    field.handleChange(
                        (selectedLevel as BaseLevel | undefined)?.level_id ??
                            null,
                    );
                    setBackgroundSelectorOpen(false);
                }}
            />
        </FieldLabelWithError>
    );
}
